/* global _ */
import { nodeListToArray, getTagNameFromNodeList } from './utils';

enum SwitchBehavior {
	SCROLL,
	CLICK,
}

const GRID_GUTTER_WIDTH = 12;

const BEHAVIOR_BREAKPOINT = 768;
const HAS_SCROLLTO_FUNCTION =
	typeof document.createElement('div').scrollTo === 'function';

function getSwitchBehavior() {
	// Todo: detect touch?
	return HAS_SCROLLTO_FUNCTION ? SwitchBehavior.SCROLL : SwitchBehavior.CLICK;
}

class Indicator {
	el: HTMLElement;
	cachedScreen: number;
	cachedTotal: number;
	handleGoToScreen: (screen: number) => void;

	constructor(
		el: HTMLElement | null,
		parent: HTMLElement,
		handleGoToScreen?: (screen: number) => void
	) {
		this.el = el || document.createElement('div');
		if (handleGoToScreen) {
			this.handleGoToScreen = handleGoToScreen;
		}
		this.el.classList.add('scrollable-grid__indicator', 'indicator');
		parent.appendChild(this.el);
	}

	set(screen: number, total: number) {
		let wasRebuilt = false;

		if (total !== this.cachedTotal) {
			this.rebuild(total);
			this.cachedTotal = total;
			wasRebuilt = true;
		}

		if (screen !== this.cachedScreen || wasRebuilt) {
			const dots = nodeListToArray(this.el.querySelectorAll('.indicator__dot'));
			dots.forEach((dot, i) => {
				dot.classList.remove('indicator__dot--active');
				if (i === screen) {
					dot.classList.add('indicator__dot--active');
				}
			});
			this.cachedScreen = screen;
		}
	}

	rebuild(total: number) {
		while (this.el.firstChild) {
			this.el.removeChild(this.el.firstChild);
		}

		for (let i = 0; i < total; i += 1) {
			this.el.appendChild(this.createDot(i));
		}
	}

	private createDot(i: number): HTMLElement {
		const dot = document.createElement('span');
		dot.classList.add('indicator__dot');

		dot.addEventListener('click', () => {
			this.handleGoToScreen(i);
		});

		return dot;
	}
}

class ScrollableGrid {
	el: HTMLElement;
	wrapper: HTMLElement;
	listElements: HTMLElement[];
	slider: HTMLElement;
	filler: HTMLElement;
	indicator: Indicator;
	cachedOffset: number;
	cachedScreen: number; // The last screen that the user navigated to

	switchBehavior: SwitchBehavior | null;

	constructor(el: HTMLElement) {
		this.el = el;
		this.wrapper = document.createElement('div');
		this.indicator = new Indicator(null, this.wrapper, this.goToScreen);
		this.listElements = nodeListToArray(
			this.el.querySelectorAll('[class*="col-"]')
		);
		this.slider = this.el.querySelector('.row-slider');

		// Insert filler element at the end of the list
		const listTagName = getTagNameFromNodeList(this.slider.childNodes) || 'div';
		this.filler = document.createElement(listTagName);
		this.filler.classList.add('scrollable-grid__filler');
		this.slider.appendChild(this.filler);

		// Wrap the element
		this.wrapper.classList.add('scrollable-grid__wrapper');
		this.el.parentNode.appendChild(this.wrapper);
		this.wrapper.appendChild(this.el);

		// Insert buttons
		const buttonPrevious = createButton('Bakåt', [
			'scrollable-grid__button',
			'scrollable-grid__button--previous',
		]);
		const buttonNext = createButton('Framåt', [
			'scrollable-grid__button',
			'scrollable-grid__button--next',
		]);

		buttonPrevious.addEventListener('click', this.goToPrevious, false);
		buttonNext.addEventListener('click', this.goToNext, false);

		this.wrapper.appendChild(buttonPrevious);
		this.wrapper.appendChild(buttonNext);

		// Attach debounced events
		const handleScrollDebounced = _.throttle(this.handleScroll.bind(this), 150);
		this.el.addEventListener('scroll', handleScrollDebounced);

		const handleResizeDebounced = _.debounce(
			this.handleResize.bind(this),
			100,
			{
				trailing: true,
				leading: true,
			}
		);
		window.addEventListener('resize', handleResizeDebounced);

		// Update state
		this.updateSwitchBehavior();
		this.updateFiller();
		this.updateIndicator();
	}

	getOffset() {
		switch (this.switchBehavior) {
			case SwitchBehavior.CLICK:
				return this.cachedOffset;
			case SwitchBehavior.SCROLL:
				return this.el.scrollLeft;
			default:
				return 0;
		}
	}

	activeScreen() {
		const scrollLeft = Math.max(0, this.getOffset());
		return Math.floor(scrollLeft / this.getSliderWidth() * this.countScreens());
	}

	countScreens() {
		return Math.ceil(this.getSliderWidth() / this.getElementWidth());
	}

	getElementWidth() {
		return this.el.clientWidth + GRID_GUTTER_WIDTH * 2;
	}

	getSliderWidth() {
		return this.listElements.reduce((a, c) => a + c.offsetWidth, 0);
	}

	updateSwitchBehavior() {
		const behaviorShouldBe = getSwitchBehavior();
		if (
			this.switchBehavior === null ||
			behaviorShouldBe !== this.switchBehavior
		) {
			switch (behaviorShouldBe) {
				case SwitchBehavior.CLICK:
					this.switchBehavior = SwitchBehavior.CLICK;
					this.wrapper.classList.add('scrollable-grid__wrapper--click');
					this.wrapper.classList.remove('scrollable-grid__wrapper--scroll');
					break;
				case SwitchBehavior.SCROLL:
					this.switchBehavior = SwitchBehavior.SCROLL;
					this.wrapper.classList.remove('scrollable-grid__wrapper--click');
					this.wrapper.classList.add('scrollable-grid__wrapper--scroll');
					break;
			}

			// Reset position
			this.slider.style.transform = '';
			// IE 11 doesn't have scrollTo in elements
			if ('scrollTo' in this.el) {
				this.el.scrollTo(0, 0);
			}
			this.goToScreen(0);
		}
	}

	updateFiller() {
		const elementsWidth = this.getSliderWidth();

		const scrollable = elementsWidth / this.getElementWidth();
		const screens = Math.ceil(scrollable);

		this.filler.style.width = `${(screens - scrollable) *
			this.getElementWidth() -
			GRID_GUTTER_WIDTH}px`;
	}

	updateIndicator() {
		this.indicator.set(this.activeScreen(), this.countScreens());
	}

	handleScroll(ev: MouseEvent) {
		this.cachedOffset = ev.target.scrollLeft as number;
		this.updateIndicator();
	}

	handleResize(ev: MouseEvent) {
		this.updateSwitchBehavior();
		this.updateFiller();
		this.goToScreen(this.cachedScreen);
		this.updateIndicator();
	}

	goToScreen = (screen: number) => {
		const offset = screen * this.getElementWidth();
		this.cachedOffset = offset;
		this.cachedScreen = screen;

		switch (this.switchBehavior) {
			case SwitchBehavior.CLICK:
				this.slider.style.transform = `translateX(${-offset}px)`;
				break;
			case SwitchBehavior.SCROLL:
				this.el.scrollTo(offset, 0);
				break;
		}
		this.updateIndicator();
	};

	goToNext = (ev: MouseEvent) => {
		let nextScreen = this.activeScreen() + 1;
		if (nextScreen > this.countScreens() - 1) {
			nextScreen = 0;
		}
		this.goToScreen(nextScreen);
	};

	goToPrevious = (ev: MouseEvent) => {
		let previousScreen = this.activeScreen() - 1;
		if (previousScreen < 0) {
			previousScreen = this.countScreens() - 1;
		}
		this.goToScreen(previousScreen);
	};
}

export default ScrollableGrid;

function createButton(body: string, classNames: string | string[]) {
	const button = document.createElement('button');

	button.innerText = body;
	if (typeof classNames === 'string') {
		classNames = [classNames];
	}

	classNames.map(cn => button.classList.add(cn));

	return button;
}
